/// <amd-module name="Dashboards/WidgetContracts/Internal/WidgetHostProps" />
import { WidgetPresentationMode, WidgetSize, WidgetTeamContext } from "../WidgetProps";
import { DashboardScope } from "Dashboards/Common/Contracts/Dashboard";
import { DashboardWidgetProps, WidgetGridSize } from "./DashboardWidgetProps";

export interface GridItemPosition {
    id: string;
    row: number;
    rowSpan: number;
    column: number;
    columnSpan: number;
}

export interface WidgetHostBehaviorProps {
    /** Indicates if the Widget should show loading animations. */
    showLoadAnimations?: boolean;

    /** Indicates if the widget should prefer opening links in a new tab. */
    openLinksInNewTab?: boolean;

    /** Optional prop for ensuring widget title is omitted in cases such as where a widget shares the same title as a parent component, where the title would be redundant.
     *  Consumers are responsible for acting on this hint in the widget and testing the widget. */
    shouldNotShowTitle?: boolean;

    /**
     * Indicates if the widget is rendering in a context where showing modal UI is unsupported.
     * For example, when a widget is rendered in a lightbox, which already is a modal experience,
     * rendering further modal experiences is problematic.
     */
    avoidShowingModalUI?: boolean;
}

/** Provides constraints and context for hosting a Widget.  */
export interface WidgetHostProps extends WidgetHostBehaviorProps {
    /** Configuration information for widget to properly instantiate and render . */
    widgetProps: DashboardWidgetProps;

    /** Description of where the Widget is being hosted.
     * Drives contextual differences of widget behavior, such as animations. */
    widgetPresentationMode: WidgetPresentationMode;

    /** Identifies the page the widget is being rendered on. Used for telemetry. */
    parentPageName: string;

    /* For Dashboard View Mode: Indicates if the user can initiate configuration and delete operations.
     * This option is ignored when a dashboard is in Edit mode. */
    userCanConfigure?: boolean;

    /** A conversion description for the widget to convert grid dimensions into pixels */
    convertToPixelSize: (size: WidgetGridSize) => WidgetSize;

    /** Optional props for parent of WidgetHost to recieve callbacks/notifications about activity in the Widget and WidgetHost.*/
    widgetHostParentProps?: WidgetHostParentProps;

    /** Describes the default team a widget should be operating under. */
    teamContext: WidgetTeamContext;

    /** Optional. Passed true then focus by default goes to more actions button of the widget.*/
    shouldFocusMoreActionsButtonByDefault?: boolean;
}

export enum WidgetHostLoadOutcome {
    /** The widget reported back with a successful load. */
    Success,

    /** The widget recognized a problem occuring within itself, and gracefully reported it to the WidgetHost. */
    HandledFailure,

    /** The widget had an uncaught exception which the WidgetHost handled on behalf of the widget. */
    UnhandledFailure,

    /** The widget didn't respond to the WidgetHost before the timeout elapsed. */
    Timeout,

    /** The WidgetHost found that the contribution describing this widget is gone, while a public/anonymous user of this widget was attempting to view it.
     * The main culprit for this is when a contribution's member-only claims are unsatisfied, but the exact cause cannot be determined by the client at the time of this check.
     * There is no way for the widget to be rendered in this case. */
    AccessNotSupported,

    /** The WidgetHost found that the contribution describing this widget is gone.
     * There is no way for the widget to be rendered in this case. */
    ExtensionDisabled
}

/** Information about a loaded widget */
export interface WidgetHostLoadedEventInfo {
    widget: DashboardWidgetProps;
    outcome: WidgetHostLoadOutcome;
}

/** Optional props for parent of the WidgetHost to recieve callbacks/notifications. */
export interface WidgetHostParentProps {
    /**  Callback for Dashboards to remove this widget based on context menu interaction. */
    onDeleteWidget?(widget: DashboardWidgetProps): void;

    /**  Callback for Dashboards to configure this widget based on context menu interaction, or from widget request to re-configure. */
    onConfigureWidget?(widget: DashboardWidgetProps): void;

    /**  Callback requesting a page to add a copy of a widget to a target dashboard, and notify the user of outcome.
     * This occurs after a user has already selected a destination dashboard.
     */
    onCopyWidget?(widgetCopyInfo: WidgetCopyProps): void;

    /** Callback to indicate widget has finished first-time load. */
    onWidgetLoaded(eventInfo: WidgetHostLoadedEventInfo): void;

    /** Callback to indicate the widget has moved */
    onWidgetMoved?(position: GridItemPosition[]): void;
}

// Used for Copy experience from a Widget

export interface WidgetCopyProps {
    /**
     *  Identifes the dashboard the user wants the widget copied to.
     */
    destinationDashboard: DashboardIdentification;

    /**
     *  Configuration information describing the Widget to be copied.
     */
    sourceWidget: DashboardWidgetProps;
}

/** A minimal description of a dashboard for identification purposes */
export interface DashboardIdentification {
    eTag: string;
    /**
     * ID of the Dashboard. Provided by service at creation time.
     */
    id: string;
    /**
     * Name of the Dashboard.
     */
    name: string;
    /**
     * ID of the Owner for a dashboard. For any legacy dashboards, this would be the unique identifier for the team associated with the dashboard.
     */
    ownerId: string;

    /**
     * For team dashboards, this is the ID of the owning team. Otherwise this is undefined.
     */
    groupId?: string;

    /**
     * Identifies the scope of the dashboard storage and permissions
     */
    dashboardScope: DashboardScope;
}
